
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useFolderList, useCreateFolder, useDeleteFolder } from '../../../queries/folders';
import { usePdfFields, useUpdatePdfFolder } from '../../../queries/pdfs';
import ConfirmModal from '../../../components/primitives/ConfirmModal';
import FolderSelectList from '../../../components/folder/FolderSelectList';
import FolderCreateForm from '../../../components/folder/FolderCreateForm';
import SubPageHeader from '../../../components/layout/SubPageHeader';

interface PdfData {
  id: string;
  file_name: string;
  folder_id?: string | null;
}

const FOLDER_COLORS = [
  { value: '#6366F1', label: '인디고' },
  { value: '#8B5CF6', label: '보라' },
  { value: '#EC4899', label: '핑크' },
  { value: '#EF4444', label: '빨강' },
  { value: '#F97316', label: '주황' },
  { value: '#EAB308', label: '노랑' },
  { value: '#22C55E', label: '초록' },
  { value: '#14B8A6', label: '청록' },
];

export default function FolderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pdfId = searchParams.get('id');
  const [userId, setUserId] = useState<string | null>(null);
  const { data: pdfData = null, isLoading: pdfLoading } = usePdfFields<PdfData>(pdfId, 'id, file_name, folder_id');
  const { data: folders = [] } = useFolderList(userId);
  const createFolderMutation = useCreateFolder();
  const deleteFolderMutation = useDeleteFolder();
  const updatePdfFolderMutation = useUpdatePdfFolder();
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#6366F1');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const loading = pdfLoading;

  /** 폴더 삭제 확인 모달 상태 */
  const [deleteFolderConfirmModal, setDeleteFolderConfirmModal] = useState<{ isOpen: boolean; folderId: string | null; folderName: string }>({
    isOpen: false,
    folderId: null,
    folderName: '',
  });

  useEffect(() => {
    if (!pdfId) {
      navigate('/dashboard');
      return;
    }
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/login');
          return;
        }
        setUserId(user.id);
      } catch {
        navigate('/login');
      }
    };
    checkAuth();
  }, [pdfId, navigate]);

  useEffect(() => {
    if (pdfData?.folder_id) {
      setSelectedFolder(pdfData.folder_id);
    }
  }, [pdfData]);

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) return;
    if (!userId) return;
    await createFolderMutation.mutateAsync({ userId, name: newFolderName.trim(), color: newFolderColor });
    setNewFolderName('');
    setNewFolderColor('#6366F1');
    setIsAddingFolder(false);
  };

  /**
   * 폴더 삭제 확인 모달을 엽니다.
   * @param folderId - 삭제할 폴더 ID
   */
  const handleDeleteFolder = (folderId: string) => {
    const folder = folders.find((f) => f.id === folderId);
    setDeleteFolderConfirmModal({ isOpen: true, folderId, folderName: folder?.name ?? '' });
  };

  /** 폴더 삭제 확인 후 실제 삭제를 수행합니다. */
  const confirmDeleteFolder = async () => {
    if (!deleteFolderConfirmModal.folderId) return;
    await deleteFolderMutation.mutateAsync(deleteFolderConfirmModal.folderId);
    if (selectedFolder === deleteFolderConfirmModal.folderId) {
      setSelectedFolder(null);
    }
    setDeleteFolderConfirmModal({ isOpen: false, folderId: null, folderName: '' });
  };

  const handleSave = async () => {
    if (!pdfId) return;
    setSaving(true);
    try {
      await updatePdfFolderMutation.mutateAsync({ pdfId, folderId: selectedFolder });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      console.error('저장 오류:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConfirmModal
        isOpen={deleteFolderConfirmModal.isOpen}
        onClose={() => setDeleteFolderConfirmModal({ isOpen: false, folderId: null, folderName: '' })}
        onConfirm={confirmDeleteFolder}
        iconClass="ri-folder-line"
        title={t('dashboard.delete_folder_confirm')}
        description={
          <>
            {t('dashboard.delete_folder_name', { name: deleteFolderConfirmModal.folderName })}
            <br />
            {t('dashboard.delete_folder_warning')}
          </>
        }
        cancelLabel={t('common.cancel')}
        confirmLabel={t('common.delete')}
      />

      <SubPageHeader
        title={t('dashboard.folder_move')}
        subtitle={pdfData?.file_name?.replace('.pdf', '')}
        saving={saving}
        saveSuccess={saveSuccess}
        savingLabel={t('common.saving')}
        saveCompleteLabel={t('common.save_complete')}
        saveLabel={t('common.save')}
        onBack={() => navigate('/dashboard')}
        onSave={handleSave}
      />

      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        {/* 현재 파일 정보 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-file-pdf-line text-2xl text-indigo-500"></i>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">{pdfData?.file_name?.replace('.pdf', '')}</h3>
              <p className="text-xs text-gray-500">
                {selectedFolder
                  ? t('dashboard.current_folder', { name: folders.find(f => f.id === selectedFolder)?.name || '' })
                  : t('dashboard.no_folder')}
              </p>
            </div>
          </div>
        </div>

        {/* 폴더 선택 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-bold text-gray-900">{t('dashboard.select_folder')}</h2>
            <button
              onClick={() => setIsAddingFolder(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              {t('dashboard.new_folder')}
            </button>
          </div>

          <FolderSelectList
            folders={folders.map((f) => ({ id: f.id, name: f.name, color: f.color }))}
            selectedFolderId={selectedFolder}
            noneLabel={t('dashboard.no_folder')}
            onSelect={setSelectedFolder}
            onDelete={handleDeleteFolder}
          />

          {isAddingFolder && (
            <div className="mt-4">
              <FolderCreateForm
                name={newFolderName}
                color={newFolderColor}
                colors={FOLDER_COLORS}
                placeholder={t('dashboard.folder_name_placeholder')}
                addLabel={t('common.add')}
                cancelLabel={t('common.cancel')}
                onChangeName={setNewFolderName}
                onChangeColor={setNewFolderColor}
                onSubmit={handleAddFolder}
                onCancel={() => {
                  setIsAddingFolder(false);
                  setNewFolderName('');
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
